import React, { useState, useEffect, useMemo } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import VendorList from 'features/Vendors/components/VendorList'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { projectTasksPath } from 'utils/projectPath'
import axios from 'axios'
import {
	MessageCircle,
	ChevronRight,
	MapPin,
	FolderOpen,
	Handshake,
	GitBranch,
	ListTodo,
	Users,
	Clock,
	ClipboardCheck,
	X,
} from 'lucide-react'
import DashboardCard from 'features/Home/components/DashboardCard'
import { DailyBriefingTrigger } from 'features/Home/components/DailyBriefing'
import { useDailyBriefing } from 'features/Home/context/DailyBriefingProvider'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'
import useCapabilities from 'hooks/useCapabilities'
import { usePersistedToggle, viewportAtLeast } from 'hooks/usePersistedToggle'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { Avatar as AvatarCN, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import TaskCNDetailSheet from 'screens/taskscn/TaskCNDetailSheet'
import PortfolioBlank from 'images/Portfolio_Blank.png'
import { Spinner } from 'components/ui/spinner'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { PortfolioCircleAvatar } from 'components/PortfolioCircleAvatar'
import { TableSortIcon, SortOrder } from 'components/TableSortIcon'
import { sortTasks, formatScheduledTime } from 'utils/tasks'
import { initializeActiveLocations } from 'slices/locationSlice'
import { initializePortfolios } from 'slices/portfolioSlice'
import { initializeMyProjects } from 'slices/projectSlice'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { initializeEvaluations } from 'slices/evaluationSlice'
import { ChatHeaderListContainer, useChatPopup } from 'features/Chat'
import { getChatHeadersByUID } from 'slices/chatSlice'
import { getUserDetails } from 'slices/authSlice'

const PROJECT_COLORS = [
	'#3b82f6',
	'#8b5cf6',
	'#ec4899',
	'#f59e0b',
	'#10b981',
	'#06b6d4',
	'#6366f1',
	'#f43f5e',
	'#84cc16',
	'#14b8a6',
]

function getProjectColor(id: string): string {
	let hash = 0
	for (let i = 0; i < id.length; i++) {
		hash = id.charCodeAt(i) + ((hash << 5) - hash)
	}
	return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length]
}

// Sortable columns in the Tasks card. `id` is the sort key, not necessarily a
// field on the task: `assignedTo` and `dateDue` need derived values.
const TASK_SORT_COLUMNS = [
	{ id: 'assignedTo', label: 'Assigned' },
	{ id: 'taskName', label: 'Task' },
	{ id: 'taskPriority', label: 'Priority' },
	{ id: 'dateDue', label: 'Due Date' },
]

// One fixed height for the four asset cards, the 346px they had before MYD-530,
// so a long list scrolls inside its card instead of stretching the row.
const ASSET_CARD_CLASS = 'h-[346px]'

const HomeWorkingCN = () => {
	const dispatch = useDispatch()
	// The global chat is a popup, not a page, so every chat affordance here
	// opens it in place rather than navigating away from the dashboard.
	const { open: openChat } = useChatPopup()
	const navigate = useNavigate()
	// Community accounts cannot start an evaluation (MYD-633), so their empty
	// state does not invite them to.
	const { canCreateAssets } = useCapabilities()

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const { openPartner, partnerDialog } = usePartnerProfileDialog()
	// Open by default only where there is room for it beside Tasks.
	const [chatsOpen, setChatsOpen] = usePersistedToggle('mdt.home.chatsOpen', () => viewportAtLeast(1280))
	const [upcomingTasks, setUpcomingTasks] = useState<any[]>([])
	const [upcomingLoaded, setUpcomingLoaded] = useState(false)
	const [selectedTask, setSelectedTask] = useState<any>(null)
	// null = server order, which is already due-date ascending from
	// /api/taskcns/upcoming. Sorting is opt-in so the default view still leads
	// with what is most overdue.
	const [taskOrderBy, setTaskOrderBy] = useState<string | null>(null)
	const [taskOrder, setTaskOrder] = useState<SortOrder>('asc')

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo: user, isInitialized: userInitialized } = userLogin
	const userId = user._id

	// The Daily Briefing (MYD-644). Its popup lives above the routes
	// (DailyBriefingProvider, MYD-645) so a link in it does not close it; Home
	// only shows the header label that toggles it. Null where it is not offered.
	const briefing = useDailyBriefing()

	const {
		entities: projectEntities,
		ids: projectIds,
		isInitialized: myProjectsInitialized,
	} = useSelector((state: any) => state.projects)
	const projects = projectIds.map(id => projectEntities[id])

	const { isInitialized: chatsInitialized, ids: chatHeaderIds } = useSelector((state: any) => state.chatHeaders)
	const {
		entities: locations,
		ids: locationIds,
		isInitialized: locationsInitialized,
	} = useSelector((state: any) => state.locations)
	const locationsByDays = locationIds.map(id => locations[id])
	const firstLocation = locations[locationIds[0]]

	const {
		entities: portfolios,
		ids: portfolioIds,
		isInitialized: portfoliosInitialized,
	} = useSelector((state: any) => state.portfolios)
	const portfoliosByDays = portfolioIds.map(id => portfolios[id])

	const {
		entities: myPartners,
		ids: myPartnerIds,
		isInitialized: myPartnersInitialized,
	} = useSelector((state: any) => state.myPartners)
	const myPartnersList = myPartnerIds.map(id => myPartners[id])

	const {
		entities: evaluations,
		ids: evaluationIds,
		isInitialized: evaluationsInitialized,
	} = useSelector((state: any) => state.evaluations)
	const evaluationsList = evaluationIds.map(id => evaluations[id])

	useEffect(() => {
		if (!userInitialized) dispatch(getUserDetails())
		if (!chatsInitialized) dispatch(getChatHeadersByUID())
		dispatch(initializeActiveLocations())
		dispatch(initializeMyPartners())
		dispatch(initializePortfolios())
		dispatch(initializeMyProjects())
		// @ts-ignore
		dispatch(initializeEvaluations())
	}, [
		dispatch,
		chatsInitialized,
		myPartnersInitialized,
		user,
		userId,
		locationsInitialized,
		portfoliosInitialized,
		userInitialized,
		myProjectsInitialized,
		evaluationsInitialized,
	])

	useEffect(() => {
		if (user?.token && !upcomingLoaded) {
			axios
				.get('/api/taskcns/upcoming', {
					params: { userid: userId },
					headers: { Authorization: `Bearer ${user.token}` },
				})
				.then(res => {
					setUpcomingTasks(res.data || [])
					setUpcomingLoaded(true)
				})
				.catch(() => {
					setUpcomingLoaded(true)
				})
		}
	}, [user, userId, upcomingLoaded])

	const sortedUpcomingTasks = useMemo(
		() => sortTasks(upcomingTasks, taskOrderBy, taskOrder),
		[upcomingTasks, taskOrderBy, taskOrder]
	)

	const handleTaskSort = (columnId: string) => {
		if (taskOrderBy === columnId) {
			setTaskOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setTaskOrderBy(columnId)
			setTaskOrder('asc')
		}
	}

	const allInitialized =
		userInitialized &&
		chatsInitialized &&
		locationsInitialized &&
		portfoliosInitialized &&
		myPartnersInitialized &&
		myProjectsInitialized &&
		upcomingLoaded &&
		evaluationsInitialized

	/*
		There was a second mobile home here: a card list of desktop routes,
		rendered whenever `useIsMobile` said so (below 1024px).

		It is gone because the field app is mobile MYDealTeams now. A phone never
		reaches this screen at all: `/` resolves through AppEntry, and on a phone
		that is always the field app. Keeping the branch meant maintaining the
		same navigation twice and answering "where is Projects" two ways.

		Note what this changes for a TABLET, which is the case worth watching. An
		iPad in portrait is ~820px, so it used to fall under `useIsMobile` and get
		the card list; it now gets this screen, the real one. That is the intended
		reading of "a tablet is not a phone": a tablet has the screen for the
		desktop app, and it keeps a visible switch to the field app if the user
		would rather have capture.
	*/
	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			{/* Left: Admin Panel */}
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			{/* Center + Right */}
			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN
					onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
					// The Daily Briefing (MYD-638, MYD-644), where Ask MDT is offered.
					leading={briefing && <DailyBriefingTrigger ref={briefing.triggerRef} open={briefing.open} onToggle={briefing.toggle} />}
				/>

				<div className="flex-1 flex overflow-hidden">
					{/* Main dashboard content */}
					<main className="flex-1 overflow-y-auto p-6">
						{!allInitialized ? (
							<Spinner containerHeight="50vh" />
						) : (
							<div className="space-y-6">
								{/*
									Top row: Tasks and Chats. A wrapping flex row rather than
									fixed column spans, so Chats drops below Tasks when the
									space between the side panels gets narrow, and Tasks takes
									the whole row when Chats is closed.
								*/}
								<div className="flex flex-wrap gap-6">
									<DashboardCard
										title="Tasks"
										icon={ListTodo}
										to="/taskscn"
										data-testid="home-tasks-card"
										className="h-[360px] flex-[2_1_480px] min-w-0"
										headerRight={
											<div className="flex items-center gap-3">
												{upcomingTasks.length > 0 && (
													<span className="text-xs text-gray-400">Past due &amp; next 2 weeks</span>
												)}
												{!chatsOpen && (
													<button
														type="button"
														onClick={() => setChatsOpen(true)}
														aria-label="Show chats"
														title="Show chats"
														className="p-1.5 rounded-md text-gray-500 hover:text-[#0080fe] hover:bg-white transition-colors"
													>
														<MessageCircle className="w-4 h-4" />
													</button>
												)}
											</div>
										}
									>
										{upcomingLoaded ? (
											upcomingTasks.length > 0 ? (
												<Table>
													<TableHeader>
														<TableRow className="bg-gray-50">
															{TASK_SORT_COLUMNS.map(col => (
																<TableHead
																	key={col.id}
																	className={`cursor-pointer select-none hover:bg-gray-100 transition-colors ${
																		col.id === 'taskName'
																			? 'min-w-[140px]'
																			: col.id === 'assignedTo'
																				? // Was w-10, sized for the avatar alone. The sort
																					// affordance needs room for the label plus icon.
																					'w-24 text-center'
																				: col.id === 'taskPriority'
																					? 'w-24 text-center'
																					: 'w-28 text-center'
																	}`}
																	onClick={() => handleTaskSort(col.id)}
																	aria-sort={
																		taskOrderBy === col.id ? (taskOrder === 'asc' ? 'ascending' : 'descending') : 'none'
																	}
																>
																	<span
																		className={`inline-flex items-center ${
																			col.id === 'taskName' ? '' : 'justify-center'
																		}`}
																	>
																		{col.label}
																		<TableSortIcon columnId={col.id} orderBy={taskOrderBy} order={taskOrder} />
																	</span>
																</TableHead>
															))}
															{/* Not sortable: it only has a value when a time is set. */}
															<TableHead className="w-24 text-center">Time</TableHead>
														</TableRow>
													</TableHeader>
													<TableBody>
														{sortedUpcomingTasks.map((task: any) => {
															const d = task.dateDue ? new Date(task.dateDue) : null
															const taskDate = d ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) : null
															const today = new Date()
															today.setHours(0, 0, 0, 0)
															const tomorrow = new Date(today)
															tomorrow.setDate(tomorrow.getDate() + 1)
															const overdue = taskDate ? taskDate < today : false

															let dueDateLabel = '-'
															if (taskDate) {
																if (taskDate.getTime() === today.getTime()) dueDateLabel = 'Today'
																else if (taskDate.getTime() === tomorrow.getTime()) dueDateLabel = 'Tomorrow'
																else {
																	const dateStr = taskDate.toLocaleDateString('en-US', {
																		month: 'short',
																		day: 'numeric',
																	})
																	dueDateLabel = overdue ? `${dateStr} (overdue)` : dateStr
																}
															}

															const priorityLabel = task.taskPriority?.split(' - ')[0] || ''
															const priorityStyles: Record<string, { text: string; bg: string; border: string }> = {
																P1: {
																	text: 'rgb(239, 68, 68)',
																	bg: 'rgba(239, 68, 68, 0.1)',
																	border: 'rgba(239, 68, 68, 0.2)',
																},
																P2: {
																	text: 'rgb(249, 115, 22)',
																	bg: 'rgba(249, 115, 22, 0.1)',
																	border: 'rgba(249, 115, 22, 0.2)',
																},
																P3: {
																	text: 'rgb(62, 124, 177)',
																	bg: 'rgba(62, 124, 177, 0.1)',
																	border: 'rgba(62, 124, 177, 0.2)',
																},
																P4: {
																	text: 'rgb(113, 113, 122)',
																	bg: 'rgba(113, 113, 122, 0.1)',
																	border: 'rgba(113, 113, 122, 0.2)',
																},
															}
															const pStyle = priorityStyles[priorityLabel]

															return (
																<TableRow
																	key={task._id}
																	className="cursor-pointer hover:bg-[#fffbf2]"
																	onClick={() => setSelectedTask(task)}
																>
																	<TableCell className="text-center">
																		<AvatarCN
																			className="h-7 w-7 mx-auto"
																			title={task.assignedTo?.firstName || task.assignedTo?.name || ''}
																		>
																			<AvatarImage
																				src={task.assignedTo?.image}
																				alt={task.assignedTo?.firstName || ''}
																			/>
																			<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
																				{(task.assignedTo?.firstName || task.assignedTo?.name || '?')
																					.charAt(0)
																					.toUpperCase()}
																			</AvatarFallback>
																		</AvatarCN>
																	</TableCell>
																	<TableCell>
																		<span className="text-sm font-medium text-gray-900">{task.taskName}</span>
																		{task.projectid?.projectName &&
																			(() => {
																				const color = getProjectColor(task.projectid._id || '')
																				const assetName = task.projectid.projectAsset?.assetName
																				const label = assetName
																					? `${task.projectid.projectName} - ${assetName}`
																					: task.projectid.projectName
																				const locationId = task.projectid.projectAsset?._id
																				const projectId = task.projectid._id
																				return (
																					<button
																						type="button"
																						onClick={e => {
																							e.stopPropagation()
																							if (locationId && projectId) {
																								navigate(`/locations/${locationId}/projects/${projectId}/tasks`)
																							}
																						}}
																						className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
																						style={{
																							color,
																							backgroundColor: `${color}1a`,
																							borderColor: `${color}33`,
																						}}
																					>
																						<GitBranch className="w-3 h-3" />
																						{label}
																					</button>
																				)
																			})()}
																	</TableCell>
																	<TableCell className="text-center">
																		{pStyle ? (
																			<span
																				className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold border"
																				style={{
																					color: pStyle.text,
																					backgroundColor: pStyle.bg,
																					borderColor: pStyle.border,
																				}}
																			>
																				{priorityLabel}
																			</span>
																		) : (
																			<span className="text-gray-400">-</span>
																		)}
																	</TableCell>
																	<TableCell className="text-center">
																		<span
																			className={`text-sm inline-flex items-center gap-1 ${overdue ? 'text-red-500' : 'text-gray-500'}`}
																		>
																			<Clock className="w-3 h-3" />
																			{dueDateLabel}
																		</span>
																	</TableCell>
																	<TableCell className="text-center text-sm text-gray-500">
																		{task.hasScheduledTime ? formatScheduledTime(task.dateDue) : ''}
																	</TableCell>
																</TableRow>
															)
														})}
													</TableBody>
												</Table>
											) : (
												<div className="flex items-center justify-center py-10">
													<Link to="/taskscn" className="no-underline">
														<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light">
															No past due or upcoming tasks.
														</h3>
													</Link>
												</div>
											)
										) : (
											<div className="flex items-center justify-center py-8">
												<span className="text-sm text-gray-400">Loading tasks...</span>
											</div>
										)}
									</DashboardCard>

									{/* Chat Card. Every affordance here opens the popup: the
							    global chat has no page of its own. Closable, and closed by
							    default on smaller screens (below 1280px). */}
									{chatsOpen && (
										<DashboardCard
											title="Chats"
											icon={MessageCircle}
											onTitleClick={() => openChat()}
											data-testid="home-chats-card"
											className="h-[360px] flex-[1_1_300px] min-w-0"
											headerRight={
												<button
													type="button"
													onClick={() => setChatsOpen(false)}
													aria-label="Close chats"
													title="Close chats"
													className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-white transition-colors"
												>
													<X className="w-4 h-4" />
												</button>
											}
										>
											{chatHeaderIds.length > 0 ? (
												<ChatHeaderListContainer isLink showSelected={false} />
											) : (
												<div className="flex items-center justify-center h-full p-6">
													<button
														type="button"
														onClick={() => openChat(myPartnersList.length > 0 ? { compose: true } : undefined)}
														className="no-underline"
													>
														<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light text-center">
															{myPartnersList.length > 0
																? 'Start a chat with one or more of your Partners.'
																: 'Connect with one or more Partners and interact with them via Chat.'}
														</h3>
													</button>
												</div>
											)}
										</DashboardCard>
									)}
								</div>

								{/*
									Asset cards: at most 4 across, then 3, 2 and 1 as the space
									between the side panels narrows.

									Each column's minimum is the LARGER of 280px and a quarter of
									the row (less the three 1.5rem gaps). The quarter term caps the
									row at 4, so a wide screen keeps the original quarter-width
									cards instead of fitting 5 or 6; the 280px term is what drops a
									column once a quarter would be narrower than that. Below the
									`sm` breakpoint (640px) it is a single full-width column.
								*/}
								<div className="grid gap-6 grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(max(280px,calc((100%_-_4.5rem)/4)),1fr))]">
									{/* Locations Card */}
									<DashboardCard title="Locations" icon={MapPin} to="/locations" className={ASSET_CARD_CLASS}>
										{locationsByDays.length > 0 ? (
											<div>
												{locationsByDays.map((location, index) => (
													<a
														key={index}
														href={`/locations/${location._id}/projects`}
														className="flex items-center px-4 py-2 hover:bg-gray-50 no-underline text-inherit"
													>
														<AvatarCN className="h-9 w-9 mr-3 shrink-0">
															<AvatarImage src={location.image} />
															<AvatarFallback>
																<MapPin className="w-4 h-4" />
															</AvatarFallback>
														</AvatarCN>
														<div className="flex-1 min-w-0">
															<div className="text-sm text-gray-900 truncate">
																{location.retailer ? location.retailer : location.assetName}
															</div>
															<div className="text-xs text-gray-500 truncate">{`${location.city}, ${location.stateProvince}`}</div>
														</div>
														<ChevronRight className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
													</a>
												))}
											</div>
										) : (
											<div className="flex items-center justify-center h-full p-6">
												<Link to="/locations" className="no-underline">
													<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light text-center">
														Start by adding Locations to manage Projects and related Documents, Images, Notes, ect.
													</h3>
												</Link>
											</div>
										)}
									</DashboardCard>

									{/* Projects Card */}
									<DashboardCard title="Projects" icon={GitBranch} to="/projects" className={ASSET_CARD_CLASS}>
										{projects.length > 0 ? (
											<div>
												{projects.map((project, index) => (
													<a
														key={index}
														href={projectTasksPath(project)}
														className="flex items-center px-4 py-2 hover:bg-gray-50 no-underline text-inherit"
													>
														<AvatarCN className="h-9 w-9 mr-3 shrink-0">
															<AvatarImage src={project.projectAsset?.image} />
															<AvatarFallback />
														</AvatarCN>
														<div className="flex-1 min-w-0">
															<div className="text-sm text-gray-900 truncate">{project.projectName}</div>
															<div className="text-xs text-gray-500 truncate">{project.projectAsset?.assetName}</div>
														</div>
														<ChevronRight className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
													</a>
												))}
											</div>
										) : (
											<div className="flex items-center justify-center h-full p-6">
												<Link to="/locations" className="no-underline">
													<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light text-center">
														Add Projects to Locations to manage Documents, Images, Notes, ect.
													</h3>
												</Link>
											</div>
										)}
									</DashboardCard>

									{/* Portfolios Card */}
									<DashboardCard title="Portfolios" icon={FolderOpen} to="/portfolios" className={ASSET_CARD_CLASS}>
										{portfoliosByDays.length > 0 ? (
											<div>
												{portfoliosByDays.map((portfolio, index) => (
													<a
														key={index}
														href={`/portfolios/${portfolio._id}`}
														className="flex items-center px-4 py-2 hover:bg-gray-50 no-underline text-inherit"
													>
														<PortfolioCircleAvatar
															locations={(portfolio.locations || [])
																.map((entry: any) => entry?.location)
																.filter(Boolean)
																.map((loc: any) => ({
																	imageUrl: loc?.image,
																	name: loc?.assetName || '',
																}))}
															fallbackInitial={portfolio.assetName?.charAt(0) || 'P'}
															size="md"
															className="mr-3"
														/>
														<div className="flex-1 min-w-0">
															<div className="text-sm text-gray-900 truncate">{portfolio.assetName}</div>
															<div className="text-xs text-gray-500 truncate">{`Location count: ${portfolio.locations.length}`}</div>
														</div>
														<ChevronRight className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
													</a>
												))}
											</div>
										) : (
											<div className="flex items-center justify-center h-full p-6">
												<Link to="/portfolios" className="no-underline">
													<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light text-center">
														Create Portfolios to help you track and manage multiple locations collectively.
													</h3>
												</Link>
											</div>
										)}
									</DashboardCard>

									{/* Evaluations Card */}
									<DashboardCard
										title="Evaluations"
										icon={ClipboardCheck}
										to="/evaluations"
										className={ASSET_CARD_CLASS}
									>
										{evaluationsList.length > 0 ? (
											<div>
												{evaluationsList.map((evaluation: any, index: number) => {
													const siteAvatars = (evaluation?.siteAvatars || []).map((s: any) => ({
														imageUrl: s?.image,
														name: s?.name || '',
													}))
													return (
														<a
															key={index}
															href={`/evaluations/${evaluation._id}`}
															className="flex items-center px-4 py-2 hover:bg-gray-50 no-underline text-inherit"
														>
															<PortfolioCircleAvatar
																locations={siteAvatars}
																fallbackInitial={evaluation.name?.charAt(0) || 'E'}
																size="md"
																className="mr-3"
															/>
															<div className="flex-1 min-w-0">
																<div className="text-sm text-gray-900 truncate">{evaluation.name}</div>
																<div className="text-xs text-gray-500 truncate">
																	{evaluation.status || 'DRAFT'}
																	{evaluation?.portfolio?.assetName ? ` · ${evaluation.portfolio.assetName}` : ''}
																</div>
															</div>
															<ChevronRight className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
														</a>
													)
												})}
											</div>
										) : (
											<div className="flex items-center justify-center h-full p-6">
												<Link to="/evaluations" className="no-underline">
													<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-base font-light text-center">
														{canCreateAssets
															? 'Create Evaluations to compare and score candidate locations across criteria.'
															: 'Evaluations shared with you will appear here.'}
													</h3>
												</Link>
											</div>
										)}
									</DashboardCard>
								</div>
							</div>
						)}
					</main>

					{/* Right: Partners & Vendor Partnerships. Closable; a drawer below 1024px. */}
					<RightPanel storageKey="home" label="Partnerships panel">
						{/* Partners card — top half */}
						<div className="flex flex-col h-1/2 border-b border-gray-200">
							<div className="px-4 py-3 border-b border-gray-100">
								<Link
									to="/partners"
									className="flex items-center gap-2 text-gray-600 text-base font-semibold no-underline hover:scale-105 transition-transform duration-150"
								>
									<Users className="w-4 h-4" /> My Partnerships
								</Link>
							</div>
							<div className="flex-1 overflow-y-auto">
								{myPartnersList.length > 0 ? (
									<div>
										{myPartnersList.map((partner, index) => (
											<button
												key={index}
												type="button"
												onClick={() => openPartner(partner.partner?._id, partner.partner)}
												className="w-full flex items-center px-4 py-2 text-left hover:bg-gray-50"
											>
												<AvatarCN className="h-9 w-9 mr-3 shrink-0">
													<AvatarImage src={partner.partner.image} />
													<AvatarFallback />
												</AvatarCN>
												<div className="flex-1 min-w-0">
													<div className="text-sm text-gray-900">{partner.partner.name}</div>
													<div className="text-xs text-gray-500">
														{partner.isAccepted ? partner.partner.company : 'Pending'}
													</div>
												</div>
											</button>
										))}
									</div>
								) : (
									<div className="flex items-center justify-center h-full p-4">
										<Link to="/partners" className="no-underline">
											<h3 className="text-[#0080fe] opacity-30 hover:font-semibold hover:opacity-100 hover:scale-110 duration-150 text-sm font-light text-center">
												Add Partners to build Deal Teams for full collaboration.
											</h3>
										</Link>
									</div>
								)}
							</div>
						</div>

						{/* Vendor Partnerships card — bottom half */}
						<div className="flex flex-col h-1/2">
							<div className="px-4 py-3 border-b border-gray-100">
								<span className="flex items-center gap-2 text-gray-600 text-base font-semibold hover:scale-105 transition-transform duration-150">
									<Handshake className="w-4 h-4" /> Vendor Partnerships
								</span>
							</div>
							<div className="flex-1 overflow-y-auto">
								<VendorList />
							</div>
						</div>
					</RightPanel>
				</div>
			</div>

			{/* Task Detail Sheet */}
			<TaskCNDetailSheet task={selectedTask} open={!!selectedTask} onClose={() => setSelectedTask(null)} />

			{partnerDialog}
		</div>
	)
}

export default HomeWorkingCN
